import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { UrlParams } from 'types';
import { PaginatedMissingAthleteReportList } from 'types/generated';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseMissingAthletes {
  ordering?: string;
  enabled?: boolean;
  csvExport?: string;
  limit?: string;
  packages?: string[];
  athleteStatuses?: string[];
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[])[];
}

function useMissingAthletes({
  ordering,
  enabled = true,
  csvExport,
  limit = '20',
  athleteStatuses,
  packages,
}: IUseMissingAthletes): UseInfiniteQueryResult<AxiosResponse<PaginatedMissingAthleteReportList>> {
  const fetchMissingAthletes = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedMissingAthleteReportList>> => {
      const params = pageParam;

      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).concat(',pk');
      }
      if (athleteStatuses) {
        params.athlete_statuses = athleteStatuses.join(',');
      }

      if (packages && packages.length > 0) {
        params.packages = packages.join(',');
      }

      return client.get(`reports/athletes/missing?${encodeUrlParams(params)}`);
    },
    [limit, ordering, packages, athleteStatuses],
  );

  return useInfiniteQuery(
    ['missing-athletes', ordering, csvExport, packages, athleteStatuses],
    fetchMissingAthletes,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
      enabled: enabled,
      staleTime: 86400000, // 1 day
    },
  );
}

export default useMissingAthletes;
